/**
 * Same-origin return paths back to the GD Contact Us embed builder, or to a main embed
 * wizard step (#1377). Rejects open redirects (external URLs, protocol-relative, non-embed
 * paths).
 */

const GD_EMBED_RETURN_RE = /^\/offices\/[^/]+\/embed\/general-dentists\/?(\?.*)?$/;
const WIZARD_STEP_RETURN_RE = /^\/offices\/[^/]+\/embed\/[a-z-]+\/?(\?.*)?$/;

export function buildGdEmbedReturnTo(officeId: string): string {
  return `/offices/${encodeURIComponent(officeId)}/embed/general-dentists`;
}

export function buildEmbedWizardReturnTo(officeId: string, step: string): string {
  return `/offices/${encodeURIComponent(officeId)}/embed/${step}`;
}

/** Returns a safe relative return path, or null if invalid. */
export function safeReturnTo(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let candidate = raw.trim();
  try {
    candidate = decodeURIComponent(candidate);
  } catch {
    return null;
  }
  if (!candidate.startsWith('/') || candidate.startsWith('//')) return null;
  if (candidate.includes('://')) return null;
  if (candidate.includes('..')) return null;
  if (/[\s\\]/.test(candidate)) return null;
  const pathOnly = candidate.split('#')[0] ?? candidate;
  if (!GD_EMBED_RETURN_RE.test(pathOnly) && !WIZARD_STEP_RETURN_RE.test(pathOnly)) return null;
  return pathOnly;
}

/** Append `returnTo` query param when the value is an allowlisted embed builder path. */
export function withReturnTo(href: string, returnTo: string | null | undefined): string {
  const ret = safeReturnTo(returnTo);
  if (!ret) return href;
  const url = new URL(href, 'http://local.invalid');
  url.searchParams.set('returnTo', ret);
  return `${url.pathname}${url.search}${url.hash}`;
}
